import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel, MaskLines } from "./Reveal";
import { mediaUrl } from "../../lib/api";
import { useContent } from "../../lib/content";
import { scrollToId } from "../../lib/smooth";

const ACCENTS = ["#ff5500", "#4fc3f7", "#3b6ef0"];

const SectorCard = ({ s, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const accent = ACCENTS[i % 3];
  return (
    <Reveal delay={i * 0.12} className={i === 1 ? "md:mt-16" : ""}>
      <article ref={ref} onMouseMove={onMove} data-testid={`sector-card-${i}`}
        className="group relative rounded-[28px] border hairline bg-[#0b1222] p-3 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: `radial-gradient(360px circle at var(--mx) var(--my), ${accent}26, transparent 60%)` }} />
        <div className="relative clip-notch aspect-[4/5] overflow-hidden rounded-[20px]">
          <motion.img style={{ y, scale: 1.18 }} src={mediaUrl(s.image)} alt={s.name} loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 transition-[filter] duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060a14] via-[#060a14]/30 to-transparent" />
          <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/90">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent, boxShadow: `0 0 10px ${accent}` }} />{s.kicker}
          </div>
          <span className="absolute top-3 right-5 font-display italic font-black text-5xl text-white/15">0{i + 1}</span>
          <div className="absolute bottom-0 inset-x-0 p-5">
            <h3 className="font-display font-extrabold text-white text-3xl sm:text-[2.1rem] leading-none">{s.name}</h3>
          </div>
        </div>
        <div className="relative p-4 pt-5">
          <p className="text-slate-300 text-[15px] leading-relaxed">{s.text}</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {(s.points || []).map((p) => (
              <li key={p} className="text-[12px] px-2.5 py-1 rounded-full border hairline text-slate-200">{p}</li>
            ))}
          </ul>
          <div className="mt-5 h-px w-full bg-white/10 overflow-hidden">
            <div className="h-full w-0 group-hover:w-full transition-[width] duration-700" style={{ background: accent }} />
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Sectors = () => {
  const h = useContent().home;
  const sectors = Array.isArray(h.sectors) ? h.sectors : [];
  return (
    <section id="settori" data-testid="sectors-section" className="relative py-24 sm:py-32 bg-[#070c18] border-y hairline">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <Reveal><SectionLabel index="04">{h.sectors_label}</SectionLabel></Reveal>
            <h2 className="mt-7 max-w-3xl font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.02]">
              <MaskLines text={h.sectors_title.replace(". ", ".\n")} lineClass={(i) => (i === 1 ? "text-frost-grad" : "")} />
            </h2>
          </div>
          <Reveal delay={0.1}>
            <button data-testid="sectors-cta" onClick={() => scrollToId("consulenza")}
              className="group inline-flex items-center gap-2 text-white border-b border-[#ff5500] pb-1 hover:text-[#ff8a4c] transition-colors">
              Verifica la disponibilità nella tua zona<ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {sectors.map((s, i) => <SectorCard key={i} s={s} i={i} />)}
        </div>
        {h.sectors_note && <Reveal><p className="mt-10 text-sm text-slate-400 font-mono">+ {h.sectors_note}</p></Reveal>}
      </div>
    </section>
  );
};
